const CALENDAR_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const STORE_CHECKOUT_TIMELINE_TIMEZONE = 'America/New_York';

const isRealCalendarDate = (year: number, month: number, day: number): boolean => {
  const candidate = new Date(Date.UTC(year, month - 1, day));

  return (
    candidate.getUTCFullYear() === year &&
    candidate.getUTCMonth() === month - 1 &&
    candidate.getUTCDate() === day
  );
};

const readEasternDateParts = (date: Date) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: STORE_CHECKOUT_TIMELINE_TIMEZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(date);

  const readPart = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');

  return {
    year: readPart('year'),
    month: readPart('month'),
    day: readPart('day'),
    hour: readPart('hour'),
  };
};

/**
 * Converts a YYYY-MM-DD picker value to Eastern start-of-day as UTC ISO.
 * Matches v4-web `formatAsEstStartOfDayIso` so HQ and v4 persist the same
 * close timestamp (04:00 UTC in EDT, 05:00 UTC in EST).
 */
const convertCalendarDateToEasternStartOfDayIso = (calendarDate: string): string | null => {
  const match = CALENDAR_DATE_PATTERN.exec(calendarDate.trim());
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (!isRealCalendarDate(year, month, day)) {
    return null;
  }

  for (let utcHour = 0; utcHour < 24; utcHour += 1) {
    const candidate = new Date(Date.UTC(year, month - 1, day, utcHour, 0, 0));
    const easternParts = readEasternDateParts(candidate);

    if (
      easternParts.year === year &&
      easternParts.month === month &&
      easternParts.day === day &&
      easternParts.hour === 0
    ) {
      return candidate.toISOString();
    }
  }

  return new Date(Date.UTC(year, month - 1, day, 4, 0, 0)).toISOString();
};

/** Converts YYYY-MM-DD picker value to Eastern start-of-day ISO for store persistence. */
export const convertStoreCheckoutCalendarDateToUtcIsoString = (calendarDate: string): string =>
  convertCalendarDateToEasternStartOfDayIso(calendarDate) ?? calendarDate;

/**
 * Persists Order Until as `group_order_date` only.
 * v4-web writes `due_date` at launch from the shipping-timeline max date, not here.
 */
export const buildStoreOrderUntilUpdatePayload = (orderId: number, calendarDate: string) => {
  const groupOrderDate = convertCalendarDateToEasternStartOfDayIso(calendarDate);

  if (!groupOrderDate) {
    return null;
  }

  return {
    order_id: orderId,
    group_order_date: groupOrderDate,
  };
};

/** Converts stored UTC ISO timestamp to YYYY-MM-DD for the date picker. */
export const convertUtcIsoStringToStoreCheckoutCalendarDate = (isoString: string): string => {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/** Converts YYYY-MM-DD order-until value to Eastern midnight ISO for shipping-timeline customDate. */
export const convertStoreCheckoutOrderUntilToTimelineCustomDate = (
  calendarDate: string
): string | null => convertCalendarDateToEasternStartOfDayIso(calendarDate);